// Ayuda del módulo Control de Ganado: guía práctica orientada al flujo del hato,
// con checklist "listo para operar" en vivo y enlaces directos a cada pantalla.
// Ganadería → Hato → Ayuda.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BadgeCheck, BookOpen, CalendarClock, CheckCircle2, ChevronDown, ChevronRight,
  ExternalLink, Loader2, XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { ganRpc } from './shared';

interface AyudaEstado {
  checklist: Record<string, boolean>;
  estado: {
    total_animales: number; hembras: number; hembras_prenadas: number; planes_activos: number;
    sanidad_vencidas: number; sanidad_proximas: number; retiros_vigentes: number; hitos_reproductivos: number;
  };
}

// ---- navegación por pestañas (deep-link dentro del SPA) ----
function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}
function Ir({ to, title, children }: { to: string; title: string; children?: React.ReactNode }) {
  const ir = useIr();
  return (
    <button onClick={() => ir(to, title)}
      className="inline-flex items-center gap-1 text-primary hover:underline text-sm font-medium">
      {children ?? title} <ExternalLink className="w-3.5 h-3.5" />
    </button>
  );
}

// ---- prerrequisitos verificados contra la BD ----
interface CheckDef { key: string; label: string; ruta: string; titulo: string; ayuda: string }

const CHECKS_ESENCIAL: CheckDef[] = [
  { key: 'predios', label: 'Registra al menos un predio (finca/UPA)', ruta: '/ganaderia/predios', titulo: 'Potreros y predios', ayuda: 'Es la base: sin predio no hay dónde ubicar potreros ni el hato.' },
  { key: 'lotes', label: 'Crea lotes por propósito (leche/carne/cría)', ruta: '/ganaderia/lotes', titulo: 'Lotes', ayuda: 'Agrupan al hato para manejo, sanidad y costeo.' },
  { key: 'animales', label: 'Da de alta el hato (animales)', ruta: '/ganaderia/hato', titulo: 'Animales', ayuda: 'Arete, sexo, raza, genealogía y ubicación de cada cabeza.' },
  { key: 'plan_sanitario', label: 'Configura un plan sanitario (vacunas/aftosa)', ruta: '/ganaderia/sanidad', titulo: 'Sanidad', ayuda: 'Calendario de vacunas, desparasitaciones y pruebas por edad o intervalo.' },
];

const CHECKS_AVANZADO: CheckDef[] = [
  { key: 'cuenta_gasto_ganaderia', label: "Mapea la cuenta contable 'gasto_ganaderia' (para contabilizar costos)", ruta: '/contabilidad/config', titulo: 'Configuración Contable', ayuda: 'Sin este mapeo, Costos no puede generar el asiento contable.' },
  { key: 'centro_costo', label: 'Crea un centro de costo (la finca) — requerido para facturar la venta', ruta: '/contabilidad/centros-costo', titulo: 'Centros de Costo', ayuda: 'Permite medir la rentabilidad de la finca y es obligatorio al facturar.' },
  { key: 'cliente', label: 'Registra un cliente (comprador de ganado)', ruta: '/financiero/partner', titulo: 'Partner', ayuda: 'Partner con tipo "cliente"; se usa en la venta y en la factura.' },
  { key: 'certificado_sri', label: 'Carga el certificado de firma (.p12) para emitir facturas de venta', ruta: '/financiero/emision-config', titulo: 'Configuración de Emisión', ayuda: 'Sin certificado, la venta se registra pero no se emite al SRI.' },
];

// ---- snapshot operativo (estado actual del hato) ----
interface EstadoItem { key: keyof AyudaEstado['estado']; label: string; ruta: string; titulo: string; tono?: 'alerta' | 'aviso' }
const ESTADO_ITEMS: EstadoItem[] = [
  { key: 'total_animales', label: 'Animales activos', ruta: '/ganaderia/hato', titulo: 'Animales' },
  { key: 'hembras', label: 'Hembras', ruta: '/ganaderia/hato', titulo: 'Animales' },
  { key: 'hembras_prenadas', label: 'Hembras preñadas', ruta: '/ganaderia/reproduccion', titulo: 'Reproducción' },
  { key: 'planes_activos', label: 'Planes sanitarios activos', ruta: '/ganaderia/sanidad', titulo: 'Sanidad' },
  { key: 'sanidad_vencidas', label: 'Aplicaciones vencidas', ruta: '/ganaderia/sanidad', titulo: 'Sanidad', tono: 'alerta' },
  { key: 'sanidad_proximas', label: 'Aplicaciones próximas', ruta: '/ganaderia/sanidad', titulo: 'Sanidad', tono: 'aviso' },
  { key: 'retiros_vigentes', label: 'Retiros vigentes (leche/carne)', ruta: '/ganaderia/sanidad', titulo: 'Sanidad', tono: 'alerta' },
  { key: 'hitos_reproductivos', label: 'Hitos reproductivos pendientes', ruta: '/ganaderia/reproduccion', titulo: 'Reproducción', tono: 'aviso' },
];

// ---- flujo de implementación ----
const FLUJO: { n: number; titulo: string; detalle: string; ruta: string; tituloTab: string }[] = [
  { n: 1, titulo: 'Registrar predios y potreros', detalle: 'La finca (UPA) y sus divisiones internas donde pastorea el hato.', ruta: '/ganaderia/predios', tituloTab: 'Potreros y predios' },
  { n: 2, titulo: 'Crear lotes por propósito', detalle: 'Leche, carne, cría o mixto — agrupan al hato para manejo, sanidad y costeo.', ruta: '/ganaderia/lotes', tituloTab: 'Lotes' },
  { n: 3, titulo: 'Dar de alta el hato', detalle: 'Arete, sexo, raza, genealogía (madre/padre) y ubicación (predio/potrero/lote) de cada animal.', ruta: '/ganaderia/hato', tituloTab: 'Animales' },
  { n: 4, titulo: 'Registrar pesajes', detalle: 'Para calcular la ganancia diaria de peso (GDP) y evaluar el desarrollo del animal.', ruta: '/ganaderia/hato', tituloTab: 'Animales' },
  { n: 5, titulo: 'Configurar el plan sanitario y aplicar campañas', detalle: 'Vacunas, desparasitaciones y pruebas; aplícalas a todo un lote de una vez (ej. aftosa).', ruta: '/ganaderia/sanidad', tituloTab: 'Sanidad' },
  { n: 6, titulo: 'Registrar la reproducción', detalle: 'Servicio → diagnóstico de preñez → parto con alta automática de la cría.', ruta: '/ganaderia/reproduccion', tituloTab: 'Reproducción' },
  { n: 7, titulo: 'Llevar el control lechero diario', detalle: 'Ordeño individual (por animal) o de tanque (total del lote), por turno AM/PM.', ruta: '/ganaderia/leche', tituloTab: 'Control lechero' },
  { n: 8, titulo: 'Registrar costos y vender ganado', detalle: 'Alimentación, sanidad, mano de obra y la venta con factura electrónica al SRI.', ruta: '/ganaderia/costos', tituloTab: 'Costos y ventas' },
];

// ---- cómo hago... ----
const COMO: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }[] = [
  {
    q: 'Registrar una campaña de vacunación a todo un lote',
    pasos: [
      'Ve a Sanidad → pestaña Eventos.',
      'Elige el ítem del plan (ej. aftosa) y, como destino, selecciona "Lote entero" en vez de un animal individual.',
      'El sistema crea el evento para cada animal activo del lote, con la misma fecha, producto y responsable.',
    ],
    enlaces: [{ to: '/ganaderia/sanidad', t: 'Sanidad' }],
  },
  {
    q: 'Registrar un parto y dar de alta la cría automáticamente',
    pasos: [
      'Ve a Reproducción → Registrar evento.',
      'Elige tipo "Parto" sobre la hembra, indica el resultado (normal/distócico/cesárea) y el sexo de la cría.',
      'Marca la opción "crear cría": el sistema da de alta un animal nuevo con arete correlativo, procedencia "nacido" y madre enlazada.',
    ],
    enlaces: [{ to: '/ganaderia/reproduccion', t: 'Reproducción' }],
  },
  {
    q: 'Interpretar el calendario sanitario (vencidas vs próximas) y los retiros',
    pasos: [
      'En Sanidad → Calendario, "vencida" es una aplicación que ya pasó su fecha (edad o intervalo) y "próxima" es la que vence pronto: ambas traen un enlace para aplicarla.',
      'Un "retiro" es el período tras aplicar un producto en que la leche o la carne del animal NO se puede vender ni consumir (retiro_leche_dias / retiro_carne_dias del plan).',
      'Mientras un animal tenga un retiro vigente, su leche debe descartarse y no debe venderse/faenarse hasta la fecha de retiro_hasta.',
    ],
    enlaces: [{ to: '/ganaderia/sanidad', t: 'Sanidad' }],
  },
  {
    q: 'Llevar el control lechero del día',
    pasos: [
      'Ve a Control lechero → Ordeño del día.',
      'Registra por animal (individual, con grasa/proteína si las mides) o de tanque (total del lote) y por turno (AM/PM o total).',
      'El resumen muestra litros totales, promedio diario y el ranking de mejores productoras.',
    ],
    enlaces: [{ to: '/ganaderia/leche', t: 'Control lechero' }],
  },
  {
    q: 'Registrar un costo y contabilizarlo',
    pasos: [
      'Ve a Costos y ventas → pestaña Costos → Registrar costo (categoría, concepto, lote/animal, valor).',
      'Usa el botón "Contabilizar" para generar el asiento; requiere tener mapeada la cuenta contable "gasto_ganaderia".',
      'Si el costo viene de un insumo de inventario (ej. balanceado) o de un evento de sanidad, puede generarse automáticamente.',
    ],
    enlaces: [{ to: '/ganaderia/costos', t: 'Costos y ventas' }, { to: '/contabilidad/config', t: 'Configuración Contable' }],
  },
  {
    q: 'Vender ganado con factura',
    pasos: [
      'Ve a Costos y ventas → pestaña Venta.',
      'Selecciona los animales, el cliente (partner tipo "cliente") y el centro de costo (la finca).',
      'Si hay certificado de firma (.p12) configurado, la venta emite la factura electrónica al SRI; si no hay certificado, la venta se registra igual pero sin emitir.',
    ],
    enlaces: [{ to: '/ganaderia/costos', t: 'Costos y ventas' }, { to: '/financiero/partner', t: 'Partner' }, { to: '/financiero/emision-config', t: 'Configuración de Emisión' }],
  },
  {
    q: 'Interpretar los KPIs reproductivos y la rentabilidad por lote',
    pasos: [
      'Tasa de preñez: % de diagnósticos con resultado "preñada" sobre el total de servicios/dx del período.',
      'IEP (intervalo entre partos): días promedio entre un parto y el siguiente de la misma hembra; entre 365-400 días es aceptable en leche.',
      'Días abiertos: días desde el parto hasta la nueva preñez; a menor número, mejor eficiencia reproductiva.',
      'La rentabilidad por lote (en Costos y ventas → Rentabilidad) cruza el costo total (por categoría) contra el ingreso de ventas de cada lote.',
    ],
    enlaces: [{ to: '/ganaderia/reproduccion', t: 'Reproducción' }, { to: '/ganaderia/costos', t: 'Costos y ventas' }],
  },
];

// ---- glosario ----
const GLOSARIO: { t: string; d: string }[] = [
  { t: 'Arete', d: 'Identificador físico único del animal (caravana); es la clave que usa todo el sistema para rastrearlo.' },
  { t: 'Semoviente', d: 'Bien mueble que se desplaza por sí mismo: término contable/legal para el ganado.' },
  { t: 'GDP (ganancia diaria de peso)', d: 'Kilos ganados por día entre dos pesajes; mide el desempeño de engorde o desarrollo.' },
  { t: 'IEP (intervalo entre partos)', d: 'Días entre un parto y el siguiente de la misma hembra; indicador clave de eficiencia reproductiva.' },
  { t: 'Días abiertos', d: 'Días desde el parto hasta lograr la siguiente preñez.' },
  { t: 'Tasa de preñez', d: '% de hembras diagnosticadas preñadas sobre el total de servicios/diagnósticos del período.' },
  { t: 'Servicio', d: 'Evento de monta natural, inseminación artificial (IA) o IATF que busca preñar a la hembra.' },
  { t: 'Dx de preñez', d: 'Diagnóstico de preñez: confirma si el servicio resultó en gestación (palpación, ecografía o prueba de sangre).' },
  { t: 'Secado', d: 'Fin voluntario del ordeño antes del parto, para que la hembra descanse (normalmente 60 días).' },
  { t: 'Retiro (leche/carne)', d: 'Días tras aplicar un producto sanitario en que la leche o la carne del animal no debe consumirse ni venderse.' },
  { t: 'Activo biológico (NIC 41)', d: 'Norma contable que rige la medición del ganado vivo (a valor razonable o costo, según el modelo elegido).' },
  { t: 'IATF', d: 'Inseminación Artificial a Tiempo Fijo: protocolo hormonal que sincroniza el celo para inseminar sin detectarlo a simple vista.' },
  { t: 'Aftosa', d: 'Fiebre aftosa: enfermedad viral de control obligatorio en el país; su vacunación es exigida por AGROCALIDAD.' },
  { t: 'SIFAE / AGROCALIDAD', d: 'Sistema de identificación del predio/hato (SIFAE) y la Agencia de Regulación y Control Fito y Zoosanitario (AGROCALIDAD), autoridad sanitaria pecuaria del Ecuador.' },
  { t: 'Guía de movilización (CZPM-M)', d: 'Documento oficial de AGROCALIDAD que autoriza y respalda el traslado o la venta de ganado entre predios/provincias.' },
];

// ---- calendario de obligaciones (AGROCALIDAD) ----
const OBLIGACIONES: { mes: number | null; texto: string }[] = [
  { mes: null, texto: 'Vacunación contra la fiebre aftosa: campañas nacionales de AGROCALIDAD (dos fases al año); es obligatoria y sin certificado vigente se bloquea la movilización/venta del ganado.' },
  { mes: null, texto: 'Registro y actualización del predio (UPA) ante AGROCALIDAD.' },
  { mes: null, texto: 'Guía de movilización (CZPM-M): tramitarla antes de movilizar o vender ganado fuera del predio.' },
  { mes: null, texto: 'Control de brucelosis y tuberculosis bovina según la categoría del hato (reproductores, lechería).' },
];

function CheckItem({ ok, label, ruta, titulo, ayuda }: { ok: boolean; label: string; ruta: string; titulo: string; ayuda: string }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b last:border-0">
      {ok ? <CheckCircle2 className="w-5 h-5 text-estable shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-urgente shrink-0 mt-0.5" />}
      <div className="min-w-0 flex-1">
        <div className="text-sm">{label}</div>
        <div className="text-xs text-muted-foreground">{ayuda}</div>
      </div>
      {!ok && <Ir to={ruta} title={titulo}>Configurar</Ir>}
    </div>
  );
}

function Acordeon({ q, pasos, enlaces }: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-lg">
      <button className="w-full flex items-center gap-2 px-4 py-3 text-left" onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        <span className="text-sm font-medium">{q}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pl-10 space-y-2">
          <ol className="list-decimal space-y-1 text-sm text-muted-foreground ml-4">
            {pasos.map((p, i) => <li key={i}>{p}</li>)}
          </ol>
          <div className="flex flex-wrap gap-3 pt-1">
            {enlaces.map((e) => <Ir key={e.to} to={e.to} title={e.t} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export function AyudaGanaderia() {
  const { data, isFetching } = useQuery<AyudaEstado>({
    queryKey: ['gan-ayuda-estado'],
    queryFn: () => ganRpc<AyudaEstado>('gan_ayuda_estado', {}),
  });
  const ch = data?.checklist ?? {};
  const CHECKS_TODOS = [...CHECKS_ESENCIAL, ...CHECKS_AVANZADO];
  const listos = CHECKS_TODOS.filter((c) => ch[c.key]).length;
  const est = data?.estado;

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div>
        <h1 className="text-xl font-semibold">Ayuda · Control de Ganado</h1>
        <p className="text-sm text-muted-foreground">Guía práctica del módulo: qué configurar primero, el flujo de implementación, cómo hacer cada proceso y a dónde ir. Los enlaces abren la pantalla correspondiente.</p>
      </div>

      {/* Listo para operar */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-primary" /> Listo para operar ({listos}/{CHECKS_TODOS.length})
            {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-1">Esencial para operar el hato:</p>
          {CHECKS_ESENCIAL.map((c) => <CheckItem key={c.key} ok={!!ch[c.key]} label={c.label} ruta={c.ruta} titulo={c.titulo} ayuda={c.ayuda} />)}
          <p className="text-xs text-muted-foreground mt-3 mb-1">Para costeo contable y venta con factura:</p>
          {CHECKS_AVANZADO.map((c) => <CheckItem key={c.key} ok={!!ch[c.key]} label={c.label} ruta={c.ruta} titulo={c.titulo} ayuda={c.ayuda} />)}
        </CardContent>
      </Card>

      {/* Estado operativo del hato */}
      {est && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Estado actual del hato</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {ESTADO_ITEMS.map((it) => {
                const valor = est[it.key];
                const alerta = it.tono === 'alerta' && valor > 0;
                const aviso = it.tono === 'aviso' && valor > 0;
                return (
                  <button key={it.key} onClick={() => useTabsStore.getState().openTab({ id: it.ruta, title: it.titulo, path: it.ruta })}
                    className="text-left rounded-lg border p-3 hover:bg-muted transition-colors">
                    <div className={`text-lg font-semibold ${alerta ? 'text-urgente' : aviso ? 'text-pendiente' : ''}`}>{valor}</div>
                    <div className="text-xs text-muted-foreground">{it.label}</div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Flujo de implementación */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarClock className="w-5 h-5 text-primary" /> El flujo de implementación</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {FLUJO.map((f) => (
            <div key={f.n} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center shrink-0">{f.n}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{f.titulo}</div>
                <div className="text-xs text-muted-foreground">{f.detalle}</div>
              </div>
              <Ir to={f.ruta} title={f.tituloTab}>Ir</Ir>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Cómo hago */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" /> ¿Cómo hago...?</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {COMO.map((c) => <Acordeon key={c.q} {...c} />)}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Calendario */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Calendario de obligaciones (AGROCALIDAD)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {OBLIGACIONES.map((o, i) => (
              <div key={i} className="text-sm flex items-start gap-2">
                <span className="text-muted-foreground shrink-0">•</span>
                <span>{o.texto}</span>
              </div>
            ))}
            <p className="text-xs text-muted-foreground pt-1">
              Estas obligaciones y sus fechas exactas deben confirmarse con AGROCALIDAD (aún no hay integración
              automática con su cronograma ni con el sistema de guías de movilización).
            </p>
          </CardContent>
        </Card>

        {/* Glosario */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Glosario</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {GLOSARIO.map((g) => (
              <div key={g.t} className="text-sm">
                <div className="font-medium">{g.t}</div>
                <div className="text-xs text-muted-foreground">{g.d}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
